import { useCallback, useMemo, useState } from 'react';

import {
	Confirm,
	Icons,
	Modal,
	NotesModal,
	PageTitle,
	Table,
} from '@components/common';

import useMutateOperationWindowRulesCreate from '@hooks/cmModule/operationWindowRules/useMutateOperationWindowRulesCreate';
import useMutateOperationWindowRulesDelete from '@hooks/cmModule/operationWindowRules/useMutateOperationWindowRulesDelete';
import useMutateOperationWindowRulesDeleteMany from '@hooks/cmModule/operationWindowRules/useMutateOperationWindowRulesDeleteMany';
import useMutateOperationWindowRulesUpdate from '@hooks/cmModule/operationWindowRules/useMutateOperationWindowRulesUpdate';
import useMutateOperationWindowRulesUpdateNotes from '@hooks/cmModule/operationWindowRules/useMutateOperationWindowRulesUpdateNotes';
import useQueryOperationWindowRules from '@hooks/cmModule/operationWindowRules/useQueryOperationWindowRules';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { TOperationWindowRuleDTO } from '@interfaces/cmModule/dto/operationWindowRulesDTO';
import { IOperationWindowRule } from '@interfaces/cmModule/operationWindowRules';
import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { ISortBy, ITablePagination, ITableSearch, ITableSort, TTableFilter } from '@interfaces/components/table';

import { defaultMeta, EAdminPanelPageTitles, EMenuActionsTypes, TMenuActionsTypes } from '@enums/common';

import { MODAL_WIDTH_580, MODAL_WIDTH_880 } from '@constants/modals';

import OperationWindowRulesForm from './OperationWindowRulesForm/OperationWindowRulesForm';
import { getOperationRulesColumns, getWorkingDaysSchedule } from './utils';

import styles from './OperationWindowRules.module.scss';

const OperationWindowRules = () => {
	const {
		accessPermission: {
			networkOperatorCmModuleAdmin,
			networkOperatorCmModuleEditor,
		},
	} = useAppSelector(auth);

	const [selectedOperationRule, setSelectedOperationRule] = useState<IOperationWindowRule | undefined>(undefined);

	const [isOperationRuleModal, setIsOperationRuleModal] = useState(false);
	const [isNotesModal, setIsNotesModal] = useState(false);
	const [isEditNotesModal, setIsEditNotesModal] = useState(false);
	const [isDeleteConfirm, setIsDeleteConfirm] = useState(false);
	const [isReadOnlyRules, setIsReadOnlyRules] = useState(false);

	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [isDirtyNotesModal, setIsDirtyNotesModal] = useState(false);

	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState('');

	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [actionType, setActionType] = useState<TMenuActionsTypes | undefined>(undefined);

	const [selectedAssigned, setSelectedAssigned] = useState<TSelectOption | null>(null);

	const isViewerOnly = !networkOperatorCmModuleAdmin && !networkOperatorCmModuleEditor;

	const openDeleteTemplateModal = useCallback(() => setIsDeleteConfirm(true), []);

	const closeDeleteTemplateModal = useCallback(() => {
		setSelectedOperationRule(undefined);
		setIsDeleteConfirm(false);
	}, []);

	const openOperationRuleModal = useCallback(() => {
		setIsOperationRuleModal(true);
	}, []);

	const closeOperationRuleModal = useCallback(() => {
		setIsOperationRuleModal(false);
		setSelectedOperationRule(undefined);
		setIsReadOnlyRules(false);
	}, []);

	const openNotesModal = useCallback((isEdit: boolean) => {
		setIsNotesModal(true);
		setIsEditNotesModal(isEdit);
	}, []);

	const closeNotesModal = useCallback(() => {
		setIsNotesModal(false);
		setSelectedOperationRule(undefined);
	}, []);

	const {
		operationWindowRules,
		countOfRecords,
		isLoadingOperationWindowRules,
	} = useQueryOperationWindowRules({
		page,
		pageSize,
		sortDirection: sortFieldName ? sortDirection : undefined,
		sortFieldName,
		searchValue,
	});

	const {
		createOperationWindowRule,
		isLoadingCreateOperationWindowRule,
	} = useMutateOperationWindowRulesCreate({ onSuccess: closeOperationRuleModal });

	const {
		updateOperationWindowRule,
		isLoadingUpdateOperationWindowRule,
	} = useMutateOperationWindowRulesUpdate({
		onSuccess: closeOperationRuleModal,
	});

	const {
		updateNotes,
		isLoadingUpdateNotes,
	} = useMutateOperationWindowRulesUpdateNotes();

	const {
		deleteOperationWindowRule,
		isLoadingDeleteOperationWindowRule,
	} = useMutateOperationWindowRulesDelete({
		onSuccess: closeDeleteTemplateModal,
	});

	const {
		deleteOperationWindowRules,
		isLoadingDeleteOperationWindowRules,
	} = useMutateOperationWindowRulesDeleteMany({
		onSuccess: closeDeleteTemplateModal,
	});

	const handleDeleteOperationWindowRule = useCallback(async () => {
		if (!selectedOperationRule) {
			await deleteOperationWindowRules({
				ids: selectedIds,
				filter: {
					page,
					pageSize,
					sortDirection,
					sortFieldName,
					searchValue,
				},
				isAll: actionType === EMenuActionsTypes.all || actionType === EMenuActionsTypes.allFiltered,
			});

			return;
		}

		await deleteOperationWindowRule({
			id: selectedOperationRule.id,
		});
	}, [
		selectedIds,
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		selectedOperationRule,
		actionType,
		deleteOperationWindowRules,
		deleteOperationWindowRule,
	]);

	const handleCustomDelete = useCallback((data: IGroupOperationWithActionDTO) => {
		openDeleteTemplateModal();
		setSelectedIds(data.ids.filter(Boolean));
		setActionType(data.actionType);
	}, [openDeleteTemplateModal]);

	const handleUpdateNotes = useCallback(async (notes: string) => {
		if (!notes || !selectedOperationRule) {
			return;
		}

		await updateNotes({
			id: selectedOperationRule.id,
			notes,
		});
	}, [selectedOperationRule, updateNotes]);

	const handleCreateOperationWindowRule = useCallback(async ({
		workingDays,
		name,
		note,
	}: TOperationWindowRuleDTO) => {
		if (selectedOperationRule) {
			await updateOperationWindowRule({
				id: selectedOperationRule.id,
				workingDays,
				name,
				note,
			});

			return;
		}

		await createOperationWindowRule({
			workingDays,
			name,
			note,
		});
	}, [
		createOperationWindowRule,
		updateOperationWindowRule,
		selectedOperationRule,
	]);

	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const search: Array<ITableSearch> = useMemo(() => [{
		initialValue: searchValue,
		name: 'searchValue',
		handler: searchHandler,
		tooltipText: 'Search by name',
	}], [searchValue, searchHandler]);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [
		page,
		pageSize,
		countOfRecords,
		pageHandler,
		pageSizeHandler,
	]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
		setPage(defaultMeta.page);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const filters: Array<TTableFilter> = useMemo(() => [{
			name: 'assigned',
			placeholder: 'Assigned',
			option: selectedAssigned,
			options: [],
			minWidth: '200px',
			setState: setSelectedAssigned,
			defaultValue: null,
		},
	], [selectedAssigned]);

	const columns = useMemo(() => getOperationRulesColumns({
		isViewerOnly,
		openNotesModal,
		openOperationRuleModal,
		openDeleteTemplateModal,
		setSelectedOperationRule,
		setIsReadOnlyRules,
	}), [
		isViewerOnly,
		openNotesModal,
		openOperationRuleModal,
		openDeleteTemplateModal,
		setSelectedOperationRule,
		setIsReadOnlyRules,
	]);

	const isLoading = isLoadingOperationWindowRules || isLoadingDeleteOperationWindowRules;
	const isDeletingOperationWindowRules = isLoadingDeleteOperationWindowRule || isLoadingDeleteOperationWindowRules;

	return (
		<div className={styles.wrapper}>
			<Modal
				isLoading={isDeletingOperationWindowRules}
				isOpen={isDeleteConfirm}
				onClose={closeDeleteTemplateModal}
			>
				<Confirm
					isLoading={isLoadingDeleteOperationWindowRule}
					onClose={closeDeleteTemplateModal}
					onSubmit={handleDeleteOperationWindowRule}
					question='Are you sure you want to delete the rule?'
				/>
			</Modal>
			<Modal
				height='600px'
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyFormModal}
				isLoading={isLoadingCreateOperationWindowRule || isLoadingUpdateOperationWindowRule}
				isOpen={isOperationRuleModal}
				isScrollableContent
				onClose={closeOperationRuleModal}
				title={isReadOnlyRules
					? 'View “Weekly” schedule'
					: `${selectedOperationRule ? 'Edit' : 'Add new'} operation window rule`
				}
				width={MODAL_WIDTH_880}
			>
				<OperationWindowRulesForm
					defaultSchedule={selectedOperationRule ? getWorkingDaysSchedule(selectedOperationRule) : undefined}
					isReadOnly={isReadOnlyRules}
					isWithoutNegativeButton={isViewerOnly}
					onSubmit={handleCreateOperationWindowRule}
					operationWindowRule={selectedOperationRule}
					setIsDirtyForm={setIsDirtyFormModal}
				/>
			</Modal>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyNotesModal}
				isOpen={isNotesModal}
				onClose={closeNotesModal}
				title='Notes'
				width={MODAL_WIDTH_580}
			>
				<NotesModal
					isEdit={isEditNotesModal}
					isLoading={isLoadingUpdateNotes}
					isReadOnly={isViewerOnly}
					name={selectedOperationRule?.name || ''}
					notes={selectedOperationRule?.note || ''}
					onSubmit={handleUpdateNotes}
					setIsDirtyForm={setIsDirtyNotesModal}
				/>
			</Modal>
			<PageTitle
				icon={Icons.configurationIcon()}
				title={EAdminPanelPageTitles.OperationRules}
			/>
			<div className={styles.tableContainer}>
				<Table
					addTooltipText='Add new template'
					columns={columns}
					data={operationWindowRules}
					filters={filters}
					handleCustomDelete={handleCustomDelete}
					isCreatable={!isViewerOnly}
					isLoading={isLoading}
					isWithoutActions={isViewerOnly}
					isWithoutDeleteAction={isViewerOnly}
					pagination={pagination}
					search={search}
					setSelectedIds={setSelectedIds}
					sort={sort}
					toggleAddModal={openOperationRuleModal}
				/>
			</div>
		</div>
	);
};

export default OperationWindowRules;
